import { z } from "zod";

/**
 * Control messages between a relay and the devices connected to it. These are plain
 * JSON text frames and carry no user data. Everything a user cares about travels as
 * opaque binary frames (see the link package) that the relay forwards without reading.
 *
 * Daemon socket (`/daemon`):
 *   relay  -> daemon  challenge | registered | peer.joined | peer.left | push.gone
 *   daemon -> relay   register | peer.close | push | pair.open | pair.close
 *   binary frames in both directions: 8-byte peer id || opaque payload
 *
 * Phone socket (`/phone?daemon=<address>`): binary frames only, payload as-is. The relay
 * adds/strips the peer id when forwarding to/from the daemon.
 */

const b64 = z.string().min(1);
const peer = z.string().regex(/^[0-9a-f]{16}$/);

export const RelayToDaemon = z.discriminatedUnion("t", [
  z.object({ t: z.literal("challenge"), nonce: b64, ephemeral: b64 }),
  z.object({ t: z.literal("registered") }),
  /**
   * A phone joined. `plan` is the relay's verdict on who it is (from the sign-in or guest token it connected with), so the computer
   * knows the phone's plan from its very first frame. Absent for an older phone that sent no token: the computer then falls back
   * to the phone's own `account.attach`.
   */
  z.object({ t: z.literal("peer.joined"), peer, plan: z.enum(["guest", "free", "pro"]).optional() }),
  z.object({ t: z.literal("peer.left"), peer }),
  /** The push service says this subscription no longer exists (the phone uninstalled or revoked it): forget it. */
  z.object({ t: z.literal("push.gone"), endpoint: z.string().max(2048) }),
]);
export type RelayToDaemon = z.infer<typeof RelayToDaemon>;

/** The words of an opted-in alert. Session facts only: which agent, which folder, the chat's name, the tool asking. */
export const PushNotice = z.object({
  title: z.string().max(120),
  body: z.string().max(200),
  eventId: z.string().min(1).max(64),
  /** The session the alert is about, so a tap can open it. Left out when the agent runs outside Brocoded (there is no session to open). */
  sessionId: z.string().min(1).max(64).optional(),
  kind: z.enum(["approval", "question", "completed", "failed"]),
});
export type PushNotice = z.infer<typeof PushNotice>;

export const DaemonToRelay = z.discriminatedUnion("t", [
  z.object({ t: z.literal("register"), id: b64, proof: b64 }),
  /** Daemon refuses this peer (e.g. an unauthorized device); relay drops its socket. */
  z.object({ t: z.literal("peer.close"), peer, code: z.number().int().optional() }),
  /**
   * Wake a phone that is not looking at the app: the relay asks the phone's push service to deliver an empty push
   * (the phone's service worker shows a fixed, content-free alert). `endpoint` is the phone's push address.
   */
  z.object({
    t: z.literal("push"),
    endpoint: z.string().min(1).max(2048),
    /** Only when the person opted in to session details: what the alert says. Never the command or the agent's own text. */
    notice: PushNotice.optional(),
    /** Web Push only: the subscription keys the payload is encrypted with. */
    keys: z.object({ p256dh: z.string().min(1).max(200), auth: z.string().min(1).max(100) }).optional(),
  }),
  /**
   * A pairing window is open: tell the relay its public lookup id (the first 4 characters of the typed code, never the secret
   * part) so a phone that types the code can find this computer. `expiresAt` is unix seconds. At most one is open per computer.
   */
  z.object({ t: z.literal("pair.open"), id: z.string().length(4), expiresAt: z.number().int() }),
  /** The window was used up, expired or cancelled. */
  z.object({ t: z.literal("pair.close"), id: z.string().length(4) }),
  /**
   * What the relay may count about this computer, so the admin can see how many are in use: its kind, the app version, how many
   * phones are paired and whether it is signed in. Sent after registering and when a phone is paired or removed. Nothing about
   * what is done on it: no sessions, commands, paths, names or phone identities.
   */
  z.object({
    t: z.literal("stats"),
    os: z.enum(["mac", "windows", "linux", "other"]),
    v: z.string().regex(/^[A-Za-z0-9._+-]{1,20}$/),
    phones: z.number().int().min(0).max(1000),
    signedIn: z.boolean(),
  }),
]);
export type DaemonToRelay = z.infer<typeof DaemonToRelay>;

/** WebSocket close codes (application range 4000-4999). */
export const CloseCode = {
  BadRequest: 4400,
  /** Registration proof missing or wrong. */
  Unauthorized: 4401,
  /** Daemon refused this device. Do not retry. */
  DeviceRejected: 4403,
  /** The daemon this phone asked for is not connected. Retry later. */
  DaemonOffline: 4404,
  /** Daemon did not register in time. */
  RegisterTimeout: 4408,
  /** A newer connection registered the same daemon address. */
  Replaced: 4409,
  TooManyPeers: 4423,
  /** A guest phone already uses another computer; the phone shows the sign-in step. Do not retry. */
  GuestComputerLimit: 4430,
  /** Phone could not keep up; reconnect and replay from the last offset. */
  SlowConsumer: 4429,
  /** The daemon this peer was talking to went away. Retry later. */
  DaemonGone: 4503,
} as const;

/**
 * First text frame a phone gets on its relay socket, before anything else: who the relay says it is. The phone shows
 * its signed-in or guest state from this, never from a late answer by the computer.
 */
export const RelayWelcome = z.object({ t: z.literal("welcome"), kind: z.enum(["guest", "account"]), plan: z.enum(["guest", "free", "pro"]) });
export type RelayWelcome = z.infer<typeof RelayWelcome>;

export const PEER_ID_BYTES = 8;

/**
 * Text frame the relay sends to phones on every ping interval. Browsers and React Native cannot
 * observe WebSocket pings, so this is how a phone notices that the path to the relay has died.
 */
export const RELAY_KEEPALIVE = "ka";
